// Email service for sending estimates and receipts
import { downloadPDF } from './pdf';
import { getEmailTemplate, getDefaultTemplates } from './templates';
import { getClientPaymentInfo } from './payments';
import { supabase } from '../lib/supabase';

// Short body for mailto so URL stays under browser/OS limits (~2KB). Full document is in the downloaded PDF.
const MAILTO_BODY = 'Please see the attached PDF document.';

// Replace template variables with actual values
const replaceTemplateVariables = async (template, document, client, fileName) => {
  const totalAmount = parseFloat(document.total || 0).toFixed(2);
  const dueDate = document.due_date ? new Date(document.due_date).toLocaleDateString() : '';
  const dueDateSection = document.due_date ? `Due Date: ${dueDate}\n` : '';
  
  // If totals are hidden, don't include total amount in email
  const hideTotals = document.hide_totals || false;
  const totalAmountSection = hideTotals ? '' : `Total Amount: $${totalAmount}\n`;

  // Get payment info if available (for paid receipts only, not unpaid invoices)
  let paymentMethod = '';
  let cardNumber = '';
  let cardNumberSection = '';
  let paymentMethodSection = '';

  const isPaidReceipt = document.type === 'invoice' && document.status === 'paid';
  
  if (isPaidReceipt) {
    try {
      const paymentInfo = await getClientPaymentInfo(client.id);
      if (paymentInfo && paymentInfo.length > 0) {
        const primaryPayment = paymentInfo.find(p => p.is_primary) || paymentInfo[0];
        paymentMethod = primaryPayment.card_type || '';
        cardNumber = primaryPayment.card_number || '';
        
        if (paymentMethod) {
          paymentMethodSection = `Payment Method: ${paymentMethod}\n`;
        }
        
        if (cardNumber) {
          // Mask card number (show last 4 digits)
          const maskedCard = cardNumber.length > 4 
            ? '**** **** **** ' + cardNumber.slice(-4)
            : cardNumber;
          cardNumberSection = `Card Number: ${maskedCard}\n`;
        }
      }
    } catch (error) {
      // Payment info not available, continue without it
      console.log('Payment info not available:', error);
    }
  }

  let userName = '';
  try {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    userName = authUser?.user_metadata?.full_name || authUser?.email || '';
  } catch {
    // User not available, leave empty
  }

  // NEW SYSTEM: First handle merge tokens with data-key attribute (if template was saved with new format)
  // Parse HTML to find merge tokens by data-key
  const parser = new DOMParser();
  const htmlDoc = parser.parseFromString(template, 'text/html');
  const mergeTokens = htmlDoc.querySelectorAll('.merge-token[data-key]');
  
  // Prepare data object for token replacement
  const templateData = {
    client: {
      name: client.name || 'Client',
    },
    invoice: {
      number: document.invoice_number || document.id,
      total: hideTotals ? '' : totalAmount,
      due_date: dueDate,
    },
    total_amount: hideTotals ? '' : totalAmount,
    total_amount_section: totalAmountSection,
    due_date: dueDate,
    due_date_section: dueDateSection,
    payment_method: paymentMethod,
    payment_method_section: paymentMethodSection,
    card_number: cardNumber,
    card_number_section: cardNumberSection,
    pdf_filename: fileName || '',
    user: {
      name: userName,
    },
  };
  
  // Replace merge tokens by reading data-key
  mergeTokens.forEach(token => {
    const key = token.getAttribute('data-key');
    if (!key) return;
    
    // Navigate nested object using dot notation
    const value = key.split('.').reduce((obj, part) => {
      if (part.match(/^\d+$/)) {
        return obj ? obj[parseInt(part)] : undefined;
      }
      return obj ? obj[part] : undefined;
    }, templateData);
    
    if (value !== undefined && value !== null) {
      token.textContent = String(value);
      token.removeAttribute('data-key');
      token.removeAttribute('data-token');
      token.classList.remove('merge-token');
    } else {
      token.remove();
    }
  });
  
  // Get updated template after token replacement
  let result = htmlDoc.body.innerHTML || template;
  
  // Fallback: Also handle old {{variable}} format for backward compatibility
  result = result
    // Old format (underscore)
    .replace(/\{\{invoice_number\}\}/g, document.invoice_number || document.id)
    .replace(/\{\{client_name\}\}/g, client.name || 'Client')
    .replace(/\{\{total_amount\}\}/g, hideTotals ? '' : totalAmount)
    .replace(/\{\{total_amount_section\}\}/g, totalAmountSection)
    .replace(/\{\{due_date\}\}/g, dueDate)
    .replace(/\{\{due_date_section\}\}/g, dueDateSection)
    .replace(/\{\{payment_method\}\}/g, paymentMethod)
    .replace(/\{\{payment_method_section\}\}/g, paymentMethodSection)
    .replace(/\{\{card_number\}\}/g, cardNumber)
    .replace(/\{\{card_number_section\}\}/g, cardNumberSection)
    .replace(/\{\{pdf_filename\}\}/g, fileName || '')
    // New format (dot notation)
    .replace(/\{\{invoice\.number\}\}/g, document.invoice_number || document.id)
    .replace(/\{\{client\.name\}\}/g, client.name || 'Client')
    .replace(/\{\{invoice\.total\}\}/g, hideTotals ? '' : totalAmount)
    .replace(/\{\{invoice\.due_date\}\}/g, dueDate)
    .replace(/\{\{user_name\}\}/g, userName)
    .replace(/\{\{user\.name\}\}/g, userName);
  
  return result;
};

export const sendEstimateEmail = async (estimate, client) => {
  try {
    // Generate and download PDF first
    const fileName = await downloadPDF(estimate, client, 'estimate');
    await new Promise(resolve => setTimeout(resolve, 500));

    // Subject from email template; short body so mailto URL stays under limits
    let emailTemplate;
    try {
      emailTemplate = await getEmailTemplate('estimate');
      if (!emailTemplate) emailTemplate = getDefaultTemplates().estimate;
    } catch (error) {
      console.log('Using default email template:', error);
      emailTemplate = getDefaultTemplates().estimate;
    }
    const subject = await replaceTemplateVariables(emailTemplate.subject, estimate, client, fileName);
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  } catch (error) {
    console.error('Error generating PDF:', error);
    let emailTemplate;
    try {
      emailTemplate = await getEmailTemplate('estimate');
      if (!emailTemplate) emailTemplate = getDefaultTemplates().estimate;
    } catch (e) {
      emailTemplate = getDefaultTemplates().estimate;
    }
    const subject = await replaceTemplateVariables(emailTemplate.subject, estimate, client, '');
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  }
};

export const sendInvoiceEmail = async (invoice, client) => {
  const isUnpaid = invoice.status !== 'paid';

  const getTemplates = async () => {
    const templateType = isUnpaid ? 'unpaid_invoice' : 'invoice';
    let emailTpl;
    try {
      emailTpl = await getEmailTemplate(templateType);
      if (!emailTpl) {
        const defaults = getDefaultTemplates();
        emailTpl = isUnpaid ? defaults.unpaid_invoice : defaults.invoice;
      }
    } catch (error) {
      console.log('Using default email template:', error);
      const defaults = getDefaultTemplates();
      emailTpl = isUnpaid ? defaults.unpaid_invoice : defaults.invoice;
    }
    return { emailTpl };
  };

  try {
    const fileName = await downloadPDF(invoice, client, 'invoice');
    await new Promise(resolve => setTimeout(resolve, 500));
    const { emailTpl } = await getTemplates();
    const subject = await replaceTemplateVariables(emailTpl.subject, invoice, client, fileName);
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  } catch (error) {
    console.error('Error generating PDF:', error);
    const { emailTpl } = await getTemplates();
    const subject = await replaceTemplateVariables(emailTpl.subject, invoice, client, '');
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  }
};

export const sendUnpaidInvoiceEmail = async (invoice, client) => {
  try {
    const fileName = await downloadPDF(invoice, client, 'invoice');
    await new Promise(resolve => setTimeout(resolve, 500));
    let emailTpl;
    try {
      emailTpl = await getEmailTemplate('unpaid_invoice');
      if (!emailTpl) emailTpl = getDefaultTemplates().unpaid_invoice;
    } catch (error) {
      console.log('Using default template:', error);
      emailTpl = getDefaultTemplates().unpaid_invoice;
    }
    const subject = await replaceTemplateVariables(emailTpl.subject, invoice, client, fileName);
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  } catch (error) {
    console.error('Error generating PDF:', error);
    let emailTpl;
    try {
      emailTpl = await getEmailTemplate('unpaid_invoice');
      if (!emailTpl) emailTpl = getDefaultTemplates().unpaid_invoice;
    } catch (err) {
      emailTpl = getDefaultTemplates().unpaid_invoice;
    }
    const subject = await replaceTemplateVariables(emailTpl.subject, invoice, client, '');
    const mailtoLink = `mailto:${client.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(MAILTO_BODY)}`;
    window.location.href = mailtoLink;
  }
};

// Replace order form template variables with actual values
// Order number is shown only as the number (no invoice URL in email); link to invoice is in the software by matching order number to invoice number.
const replaceOrderFormTemplateVariables = (template, orderData, client, user, options = {}) => {
  const clientAddress = client?.address || '';
  const clientAddressSection = clientAddress ? `Address: ${clientAddress}\n` : '';
  
  const cityStateZip = [client?.city, client?.state, client?.zip].filter(Boolean).join(', ');
  const cityStateZipSection = cityStateZip ? `City/State/Zip: ${cityStateZip}\n` : '';
  
  const deliveryDate = orderData.delivery_date ? new Date(orderData.delivery_date).toLocaleDateString() : '';
  const deliveryDateSection = deliveryDate ? `Delivery Date: ${deliveryDate}\n` : '';
  
  const priority = orderData.priority || 'normal';
  const prioritySection = priority !== 'normal' ? ` [${priority.toUpperCase()}]` : '';

  const orderNumberDisplay = orderData.order_number || '';
  const salesAssociate = user?.user_metadata?.full_name || user?.email || 'User';
  const orderDate = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });

  return template
    .replace(/\{\{client_name\}\}/g, client?.name || 'N/A')
    .replace(/\{\{client_email\}\}/g, client?.email || 'N/A')
    .replace(/\{\{client_phone\}\}/g, client?.phone || 'N/A')
    .replace(/\{\{client_address\}\}/g, clientAddress)
    .replace(/\{\{client_address_section\}\}/g, clientAddressSection)
    .replace(/\{\{client_city_state_zip\}\}/g, cityStateZip)
    .replace(/\{\{client_city_state_zip_section\}\}/g, cityStateZipSection)
    .replace(/\{\{glass_type\}\}/g, orderData.glass_type || 'N/A')
    .replace(/\{\{dimensions\}\}/g, orderData.dimensions || 'N/A')
    .replace(/\{\{quantity\}\}/g, String(orderData.quantity || 1))
    .replace(/\{\{thickness\}\}/g, orderData.thickness || 'N/A')
    .replace(/\{\{edge_work\}\}/g, orderData.edge_work || 'N/A')
    .replace(/\{\{holes_cuts\}\}/g, orderData.holes_cuts || 'N/A')
    .replace(/\{\{priority\}\}/g, priority.charAt(0).toUpperCase() + priority.slice(1))
    .replace(/\{\{priority_section\}\}/g, prioritySection)
    .replace(/\{\{delivery_date\}\}/g, deliveryDate)
    .replace(/\{\{delivery_date_section\}\}/g, deliveryDateSection)
    .replace(/\{\{special_instructions\}\}/g, orderData.special_instructions || 'None')
    .replace(/\{\{our_po\}\}/g, orderData.our_po || '')
    .replace(/\{\{order_number\}\}/g, orderNumberDisplay)
    .replace(/\{\{order_number_link\}\}/g, '')
    .replace(/\{\{to\}\}/g, orderData.to || client?.name || '')
    .replace(/\{\{sales_associate\}\}/g, salesAssociate)
    .replace(/\{\{order_date\}\}/g, orderDate)
    .replace(/\{\{service_type\}\}/g, orderData.service_type || 'Measure, Furnish and Install - MFI')
    .replace(/\{\{description\}\}/g, orderData.description || '')
    .replace(/\{\{quantity_dimensions\}\}/g, orderData.quantity_dimensions || '')
    .replace(/\{\{specifications\}\}/g, orderData.specifications || '')
    .replace(/\{\{comments\}\}/g, orderData.comments || '');
};

// Send glass order email to orderbay@cvgsonline.com
// Order number in email is just the number; link to invoice is in the software (order number matches invoice number).
export const sendOrderEmail = async (orderData, client, user) => {
  const orderEmail = 'orderbay@cvgsonline.com';
  
  // Get order form template
  let template;
  try {
    const { getOrderFormTemplate } = await import('./orderFormTemplates');
    template = await getOrderFormTemplate();
  } catch (error) {
    console.log('Using default template:', error);
    const { getDefaultOrderFormTemplate } = await import('./orderFormTemplates');
    template = getDefaultOrderFormTemplate();
  }

  const subject = replaceOrderFormTemplateVariables(template.subject, orderData, client, user);
  const body = replaceOrderFormTemplateVariables(template.body, orderData, client, user);
  
  // Open email client with pre-filled information
  const mailtoLink = `mailto:${orderEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailtoLink;
};
